import React from 'react';
import { Card, Typography, Box, IconButton, Tooltip, Avatar, useTheme, Chip } from '@mui/material';
import {
  Edit,
  Delete,
  Image as ImageIcon,
  CalendarToday as CalendarIcon,
  Article as ArticleIcon,
  AttachMoney as MoneyIcon,
  Favorite as FavoriteIcon,
  Visibility as VisibilityIcon,
  LocalOffer as TagIcon,
  FolderSpecial as CollectionIcon
} from '@mui/icons-material';

// Formatadores
const formatNumber = (num) => {
  if (!num || num === 0) return '0';
  if (num >= 1000000000) return (num / 1000000000).toFixed(1).replace(/\.0$/, '') + 'B';
  if (num >= 1000000) return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  return num.toString();
};

const formatCurrency = (value) => {
  if (!value || value === 0) return 'R$ 0';
  if (value >= 1000000) return 'R$ ' + (value / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (value >= 1000) return 'R$ ' + (value / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  return 'R$ ' + value.toFixed(2);
};

const CollectionCoverCard = ({
  collection,
  onClick,
  onEdit,
  onDelete
}) => {
  const theme = useTheme();

  const articleCount = Number(collection.articleCount ?? 0);
  const createdAt = collection.createdAt ? new Date(collection.createdAt).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }) : '';

  // Estatísticas agregadas
  const totalValuation = Number(collection.totalValuation ?? 0);
  const totalLikes = Number(collection.totalLikes ?? 0);
  const totalViews = Number(collection.totalViews ?? 0);

  // Tipo da coleção
  const isTagBased = collection.type === 'tags';
  const typeLabel = isTagBased ? 'Tags' : 'Artigos';
  const TypeIcon = isTagBased ? TagIcon : ArticleIcon;
  const typeColor = isTagBased ? 'primary' : 'secondary';

  // Quantidade de tags (para coleções de tags)
  const tagCount = collection.tags?.length || 0;

  return (
    <Card
      elevation={0}
      onClick={() => onClick?.(collection)}
      sx={{
        overflow: 'hidden',
        cursor: 'pointer',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.2s ease',
        border: '2px solid transparent',
        boxShadow: theme.palette.mode === 'dark'
          ? '0 2px 4px rgba(0, 0, 0, 0.4)'
          : '0 2px 4px rgba(0, 0, 0, 0.15)',
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: theme.palette.mode === 'dark'
            ? '0 4px 12px rgba(0, 0, 0, 0.5)'
            : '0 4px 12px rgba(0, 0, 0, 0.15)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      {/* Imagem de capa */}
      <div
        className="image-container"
        style={{
          position: 'relative',
          flexShrink: 0,
        }}
      >
        {collection.coverImage ? (
          <img
            src={collection.coverImage}
            alt={collection.name}
            style={{
              width: '100%',
              height: '220px',
              objectFit: 'cover',
              display: 'block'
            }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'https://www.mensure.com.br/public/mensure_logo.jpg';
            }}
          />
        ) : (
          <div style={{
            width: '100%',
            height: '220px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.palette.grey[800]
          }}>
            <Avatar variant="rounded" sx={{ width: 64, height: 64, bgcolor: 'grey.700' }}>
              <CollectionIcon fontSize="large" />
            </Avatar>
          </div>
        )}

        {/* Badge de tipo no canto superior esquerdo */}
        <Chip
          icon={<TypeIcon sx={{ fontSize: '0.875rem !important' }} />}
          label={typeLabel}
          size="small"
          color={typeColor}
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            zIndex: 3,
            fontWeight: 600,
            fontSize: '0.7rem',
            height: 24,
            '& .MuiChip-icon': {
              marginLeft: '6px',
              marginRight: '-2px'
            }
          }}
        />

        {/* Overlay com métricas (aparece no hover) */}
        <div
          className="metrics-overlay"
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            padding: '12px',
            background: 'rgba(0, 0, 0, 0.75)',
            backdropFilter: 'none',
            WebkitBackdropFilter: 'none',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '8px',
            opacity: 0,
            transition: 'opacity 0.3s ease',
            zIndex: 2
          }}
          onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
          onMouseLeave={(e) => e.currentTarget.style.opacity = '0'}
        >
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {/* Artigos */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <ArticleIcon sx={{ fontSize: '0.875rem', color: '#fff', opacity: 0.9 }} />
              <span style={{ fontSize: '0.75rem', color: '#fff', fontWeight: 500 }}>
                {articleCount} artigos
              </span>
            </div>

            {/* Tags (apenas para coleções de tags) */}
            {isTagBased && tagCount > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <TagIcon sx={{ fontSize: '0.875rem', color: '#fff', opacity: 0.9 }} />
                <span style={{ fontSize: '0.75rem', color: '#fff', fontWeight: 500 }}>
                  {tagCount} tags
                </span>
              </div>
            )}

            {/* Valoração */}
            {totalValuation > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <MoneyIcon sx={{ fontSize: '0.875rem', color: '#fff', opacity: 0.9 }} />
                <span style={{ fontSize: '0.75rem', color: '#fff', fontWeight: 500 }}>
                  {formatCurrency(totalValuation)}
                </span>
              </div>
            )}

            {/* Likes */}
            {totalLikes > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <FavoriteIcon sx={{ fontSize: '0.875rem', color: '#fff', opacity: 0.9 }} />
                <span style={{ fontSize: '0.75rem', color: '#fff', fontWeight: 500 }}>
                  {formatNumber(totalLikes)}
                </span>
              </div>
            )}

            {/* Views */}
            {totalViews > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <VisibilityIcon sx={{ fontSize: '0.875rem', color: '#fff', opacity: 0.9 }} />
                <span style={{ fontSize: '0.75rem', color: '#fff', fontWeight: 500 }}>
                  {formatNumber(totalViews)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Ícones de ação no canto superior direito */}
        <div style={{
          position: 'absolute',
          top: 8,
          right: 8,
          display: 'flex',
          gap: '4px',
          zIndex: 3
        }}>
          {onEdit && (
            <Tooltip title="Editar" arrow>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(collection);
                }}
                size="small"
                sx={{
                  bgcolor: 'rgba(0, 0, 0, 0.6)',
                  color: '#fff',
                  width: 32,
                  height: 32,
                  '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.8)' },
                }}
              >
                <Edit sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
          {onDelete && (
            <Tooltip title="Excluir" arrow>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(collection);
                }}
                size="small"
                sx={{
                  bgcolor: 'rgba(0, 0, 0, 0.6)',
                  color: '#fff',
                  width: 32,
                  height: 32,
                  '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.9)' },
                }}
              >
                <Delete sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Conteúdo: título e descrição */}
      <div style={{
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* Nome da coleção e data */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          marginBottom: '4px'
        }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              lineHeight: 1.3,
              flex: 1
            }}
          >
            {collection.name || 'Sem nome'}
          </Typography>
          {createdAt && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              flexShrink: 0
            }}>
              <CalendarIcon sx={{ fontSize: '0.75rem', color: 'text.disabled' }} />
              <span style={{
                fontSize: '0.75rem',
                color: theme.palette.text.disabled,
                whiteSpace: 'nowrap'
              }}>
                {createdAt}
              </span>
            </div>
          )}
        </div>

        {collection.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {collection.description}
          </Typography>
        )}
      </div>
    </Card>
  );
};

export default CollectionCoverCard;
